import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  createCodeChallenge,
  createCodeVerifier,
  createState,
  parseCallbackQuery,
  trimTrailingSlash,
} from "./pkce";
import {
  clearSession,
  expiresAtFrom,
  isUsable,
  readEmailClaim,
  readSession,
  type Session,
  type SessionStorage,
  writeSession,
} from "./session";
import type { TokenProvider } from "./tokenProvider";

/**
 * Sign-in against the Cognito hosted UI (sc-178487), authorization code with
 * PKCE.
 *
 * The app client is public — `generateSecret: false`, because a secret shipped
 * to a browser is not a secret — so PKCE is what stops a stolen `code` from
 * being redeemed by anyone else.
 *
 * **What comes back and is used is the access token, never the ID token.** A
 * remote MCP client can only ever hold an access token (sc-178757), so both
 * callers present the same credential and the API has one code path. The API
 * checks `token_use === "access"` precisely because the HTTP API's JWT
 * authorizer would otherwise accept an ID token too.
 */

/** Cognito's callback path. Must match `buildOAuthUrls` in `packages/infra`. */
export const CALLBACK_PATH = "/auth/callback";

const PKCE_KEY = "contract-manager.pkce";

/**
 * The name Cognito knows the Google provider by — what
 * `UserPoolIdentityProviderGoogle` registers, and the pool's only provider.
 */
const IDENTITY_PROVIDER = "Google";

/** Raised when there is nothing to present. The UI turns this into a sign-in prompt. */
export class NotSignedInError extends Error {
  constructor() {
    super("sign-in required");
    this.name = "NotSignedInError";
  }
}

/** Why a sign-in could not be completed, for the screen to translate. */
export type SignInErrorCode =
  | "MISSING_CODE"
  | "PENDING_NOT_FOUND"
  | "STATE_MISMATCH"
  | "TOKEN_REQUEST_FAILED";

/**
 * A sign-in that broke on our side of the exchange. What Cognito or Google
 * say in words of their own (`error_description`) stays a plain `Error`:
 * there is nothing to translate it to.
 */
export class SignInError extends Error {
  readonly code: SignInErrorCode;
  readonly params: Readonly<Record<string, string | number>>;

  constructor(
    code: SignInErrorCode,
    message: string,
    params: Readonly<Record<string, string | number>> = {},
  ) {
    super(message);
    this.name = "SignInError";
    this.code = code;
    this.params = params;
  }
}

export interface CognitoConfig {
  /** Hosted UI origin, e.g. `https://contract-manager-dev.auth.ap-northeast-1.amazoncognito.com`. */
  readonly domain: string;
  readonly clientId: string;
  /**
   * Scopes to ask for. `openid email` plus the resource server's
   * `contracts.read` / `contracts.write`, whose prefix is the resource server
   * identifier (the site origin) — so the value differs per environment and
   * has to be configuration rather than a constant.
   */
  readonly scopes: readonly string[];
}

export interface CognitoDeps {
  readonly storage: SessionStorage;
  readonly fetchFn: typeof fetch;
  readonly crypto: Crypto;
  readonly now: () => number;
  /** Where the browser goes. Injected so a test never actually navigates. */
  readonly redirect: (url: string) => void;
  /** The app's own origin, used to build the redirect and logout URIs. */
  readonly origin: string;
}

interface PendingAuth {
  readonly state: string;
  readonly verifier: string;
  /** Path to restore after the round trip, so a deep link survives sign-in. */
  readonly returnTo: string;
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

export interface CognitoAuth extends TokenProvider {
  /** The current session, or null. Synchronous: the UI renders off this. */
  currentSession(): Session | null;
  /**
   * Forget the session without leaving the site. For a 401: the token the API
   * refused is dead, and leaving it in storage would show the app again on
   * the next reload only to bounce off the following request.
   */
  dropSession(): void;
  /** Send the browser to the hosted UI. */
  signIn(returnTo?: string): Promise<void>;
  /** Redeem the code on the callback route. Returns where to go next. */
  completeSignIn(search: string): Promise<{ returnTo: string }>;
  signOut(): Promise<void>;
}

/** Cognito's default access token lifetime, used when `expires_in` is absent. */
const DEFAULT_EXPIRES_IN_SECONDS = 3600;

export function createCognitoAuth(
  config: CognitoConfig,
  deps: CognitoDeps,
): CognitoAuth {
  const domain = trimTrailingSlash(config.domain);
  const origin = trimTrailingSlash(deps.origin);
  const redirectUri = `${origin}${CALLBACK_PATH}`;

  const readPending = (): PendingAuth | null => {
    const raw = deps.storage.read(PKCE_KEY);
    if (raw === null) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<PendingAuth>;
      if (
        typeof parsed.state !== "string" ||
        typeof parsed.verifier !== "string"
      ) {
        return null;
      }
      return {
        state: parsed.state,
        verifier: parsed.verifier,
        returnTo: typeof parsed.returnTo === "string" ? parsed.returnTo : "/",
      };
    } catch {
      return null;
    }
  };

  const postToken = async (
    body: Record<string, string>,
  ): Promise<TokenResponse & { access_token: string }> => {
    const response = await deps.fetchFn(`${domain}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: config.clientId, ...body }),
    });
    const payload = (await response.json().catch(() => ({}))) as TokenResponse;
    if (!response.ok || typeof payload.access_token !== "string") {
      const reason = payload.error_description ?? payload.error;
      if (reason !== undefined) {
        throw new Error(reason);
      }
      throw new SignInError(
        "TOKEN_REQUEST_FAILED",
        `the token request failed (${response.status})`,
        { status: response.status },
      );
    }
    return { ...payload, access_token: payload.access_token };
  };

  const store = (
    payload: TokenResponse & { access_token: string },
    fallbackRefresh?: string,
  ): Session => {
    const accessToken = payload.access_token;
    // A refresh grant does not return a new refresh token; keep the one we
    // came in with, or the next renewal has nothing to present.
    const refreshToken = payload.refresh_token ?? fallbackRefresh;
    const email = readEmailClaim(accessToken);
    const session: Session = {
      accessToken,
      expiresAt: expiresAtFrom(
        deps.now(),
        payload.expires_in ?? DEFAULT_EXPIRES_IN_SECONDS,
      ),
      ...(refreshToken === undefined ? {} : { refreshToken }),
      ...(email === undefined ? {} : { email }),
    };
    writeSession(deps.storage, session);
    return session;
  };

  /**
   * Shared by every caller that finds an expiring token, so two concurrent
   * requests do not each spend the refresh token — Cognito's rotation would
   * invalidate whichever grant lost the race.
   */
  let inFlightRefresh: Promise<Session> | null = null;

  const refresh = async (refreshToken: string): Promise<Session> => {
    inFlightRefresh ??= postToken({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    })
      .then((payload) => store(payload, refreshToken))
      .finally(() => {
        inFlightRefresh = null;
      });
    return inFlightRefresh;
  };

  return {
    currentSession() {
      return readSession(deps.storage);
    },

    async getAccessToken() {
      const session = readSession(deps.storage);
      if (session === null) {
        throw new NotSignedInError();
      }
      if (isUsable(session, deps.now())) {
        return session.accessToken;
      }
      if (session.refreshToken === undefined) {
        clearSession(deps.storage);
        throw new NotSignedInError();
      }
      try {
        return (await refresh(session.refreshToken)).accessToken;
      } catch {
        // A refresh token Cognito will not honour — revoked, or past the
        // one-day validity — is indistinguishable here from being signed out.
        clearSession(deps.storage);
        throw new NotSignedInError();
      }
    },

    dropSession() {
      clearSession(deps.storage);
      deps.storage.remove(PKCE_KEY);
    },

    async signIn(returnTo = "/") {
      const verifier = createCodeVerifier(deps.crypto);
      const state = createState(deps.crypto);
      deps.storage.write(
        PKCE_KEY,
        JSON.stringify({ state, verifier, returnTo } satisfies PendingAuth),
      );
      deps.redirect(
        buildAuthorizeUrl({
          domain,
          clientId: config.clientId,
          redirectUri,
          scopes: config.scopes,
          state,
          identityProvider: IDENTITY_PROVIDER,
          codeChallenge: await createCodeChallenge(deps.crypto, verifier),
        }),
      );
    },

    async completeSignIn(search) {
      const result = parseCallbackQuery(search);
      if (result === null) {
        throw new SignInError(
          "MISSING_CODE",
          "the callback carries no authorization code",
        );
      }
      const pending = readPending();
      // Consume it either way: a verifier left behind could be replayed
      // against a later callback.
      deps.storage.remove(PKCE_KEY);

      if (result.kind === "error") {
        throw new Error(result.message);
      }
      if (pending === null) {
        throw new SignInError(
          "PENDING_NOT_FOUND",
          "no sign-in is in progress; start again",
        );
      }
      if (pending.state !== result.state) {
        // The one check that makes `state` worth sending: a callback the user
        // did not start is refused.
        throw new SignInError(
          "STATE_MISMATCH",
          "the state does not match; the sign-in was abandoned",
        );
      }

      store(
        await postToken({
          grant_type: "authorization_code",
          code: result.code,
          redirect_uri: redirectUri,
          code_verifier: pending.verifier,
        }),
      );
      return { returnTo: pending.returnTo };
    },

    async signOut() {
      clearSession(deps.storage);
      deps.storage.remove(PKCE_KEY);
      // Ends the Cognito session too. Without this the hosted UI would sign
      // the same person straight back in without asking, which does not look
      // like signing out.
      deps.redirect(
        buildLogoutUrl({
          domain,
          clientId: config.clientId,
          logoutUri: `${origin}/`,
        }),
      );
    },
  };
}
